import { HttpClient } from "../api/HttpClient";
import { BaseAuthProvider, type BaseAuthProviderConfig } from "./BaseAuthProvider";
import {
  AuthHttpError,
  type AuthEndpoints,
  type Credentials,
  type EmailConfirmationHandle,
  type PasswordCredentials,
  type RegisterCredentials,
  type Tokens,
  type User,
} from "../types";

export interface JwtProviderConfig extends BaseAuthProviderConfig {
  baseUrl: string;
  endpoints?: Partial<AuthEndpoints>;
  defaultTimeoutMs?: number;
  /** Notifications channel the email-confirmation flow sets/confirms a preference on. Default `"console"`. */
  emailConfirmationChannel?: string;
  /** Subject tag sent with the confirmation address. Default `"welcome"`. */
  emailConfirmationSubject?: string;
}

interface RawTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number; // seconds
  expiresAt?: number; // epoch ms — takes precedence over expiresIn if both present
  user?: User;
}

/**
 * Some deployments wrap responses in a `{ data: {...} }` envelope and use
 * snake_case keys (e.g. `access_token`) at the transport level rather than
 * the camelCase shape the rest of this file works with. Accept either shape
 * here so callers don't have to care which one their backend uses.
 */
function normalizeTokenResponse(raw: unknown): RawTokenResponse | null {
  if (!raw || typeof raw !== "object") return null;
  const src = (("data" in raw ? (raw as { data?: unknown }).data : raw) ?? raw) as Record<string, unknown>;
  const accessToken = (src.accessToken ?? src.access_token) as string | undefined;
  if (!accessToken) return null;
  return {
    accessToken,
    refreshToken: (src.refreshToken ?? src.refresh_token) as string | undefined,
    expiresIn: (src.expiresIn ?? src.expires_in) as number | undefined,
    expiresAt: (src.expiresAt ?? src.expires_at) as number | undefined,
    user: src.user as User | undefined,
  };
}

function toTokens(raw: RawTokenResponse): Tokens {
  return {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresAt: raw.expiresAt ?? (raw.expiresIn !== undefined ? Date.now() + raw.expiresIn * 1000 : undefined),
  };
}

/**
 * Username/password authentication against a first-party JSON backend that
 * issues short-lived access tokens plus a rotating refresh token.
 */
export class JwtProvider extends BaseAuthProvider {
  private readonly endpoints: AuthEndpoints;
  private readonly http: HttpClient;

  constructor(private readonly config: JwtProviderConfig) {
    super(config);
    this.endpoints = {
      login: "auth/login/email",
      logout: "auth/logout",
      refresh: "auth/refresh",
      register: "auth/register",
      currentUser: "me/account",
      forgotPassword: "auth/request_password_reset",
      resetPassword: "auth/confirm_password_reset",
      verifyEmail: "verify-email",
      changePassword: "me/change_password",
      ...config.endpoints,
    };
    this.http = new HttpClient({
      baseUrl: config.baseUrl,
      defaultTimeoutMs: config.defaultTimeoutMs,
      getAccessToken: () => this.getAccessToken(),
      refreshAccessToken: async () => {
        await this.refresh();
        return this.getAccessToken();
      },
      onAuthFailure: () => void this.logout(),
    });
  }

  protected async doLogin(credentials: Credentials): Promise<{ tokens: Tokens; user?: User | null }> {
    const creds = credentials as PasswordCredentials;
    const raw = await this.http.request<RawTokenResponse>(this.endpoints.login!, {
      method: "POST",
      skipAuth: true,
      body: { identifier: creds.identifier, password: creds.password, rememberMe: creds.rememberMe },
    });
    return { tokens: toTokens(raw), user: raw.user ?? undefined };
  }

  protected async doRefresh(): Promise<Tokens> {
    const current = this.session.getTokens();
    const raw = await this.http.request<RawTokenResponse>(this.endpoints.refresh!, {
      method: "POST",
      skipAuth: true,
      skipAuthRetry: true,
      body: { refreshToken: current?.refreshToken },
    });
    return toTokens(raw);
  }

  protected async doFetchCurrentUser(): Promise<User | null> {
    try {
      return await this.http.request<User>(this.endpoints.currentUser!, { method: "GET" });
    } catch {
      return null;
    }
  }

  protected async doLogout(): Promise<void> {
    if (!this.endpoints.logout) return;
    const current = this.session.getTokens();
    await this.http.request(this.endpoints.logout, {
      method: "POST",
      skipAuthRetry: true,
      body: { refreshToken: current?.refreshToken },
    });
  }

  // --- Convenience flows beyond the core AuthProvider contract ---

  async forgotPassword(identifier: string): Promise<void> {
    await this.http.request(this.endpoints.forgotPassword!, { method: "POST", skipAuth: true, body: { identifier } });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.http.request(this.endpoints.resetPassword!, {
      method: "POST",
      skipAuth: true,
      body: { token, newPassword },
    });
  }

  async verifyEmail(token: string): Promise<void> {
    await this.http.request(this.endpoints.verifyEmail!, { method: "POST", skipAuth: true, body: { token } });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.http.request(this.endpoints.changePassword!, {
      method: "POST",
      body: { currentPassword, newPassword },
    });
  }

  // --- Registration + email confirmation (implements RegistrableAuthProvider) ---

  /**
   * Create the account, then — mirroring `login()` — start an authenticated
   * session immediately from the tokens the backend returns. Throws
   * `AuthHttpError` (e.g. on a duplicate username) or if the response
   * doesn't include a session; callers typically show the error and offer a
   * "sign in instead" link.
   */
  async register(credentials: RegisterCredentials): Promise<void> {
    const raw = await this.http.request<unknown>(this.endpoints.register!, {
      method: "POST",
      skipAuth: true,
      body: { username: credentials.username, password: credentials.password },
    });

    const normalized = normalizeTokenResponse(raw);
    if (!normalized) {
      throw new AuthHttpError("Registration did not return a session.", {
        status: null,
        code: "REGISTRATION_NO_SESSION",
      });
    }

    const tokens = toTokens(normalized);
    this.session.setTokens(tokens);
    const user = normalized.user ?? (await this.doFetchCurrentUser());
    this.setCurrentUser(user);
    this.session.startIdleWatch();
    if (user) this.events.emit("login", { user });
  }

  /**
   * Ask the backend to send a confirmation code to `email`. Must be called
   * while authenticated (i.e. after `register()`). Returns a `nonce` that
   * must be passed back — unchanged — to `confirmEmailConfirmation`.
   */
  async requestEmailConfirmation(email: string): Promise<EmailConfirmationHandle> {
    const channel = this.config.emailConfirmationChannel ?? "console";
    const subject = this.config.emailConfirmationSubject ?? "welcome";
    const raw = await this.http.request<{ nonce?: string }>(`/notifications/${channel}/preferences/set`, {
      method: "POST",
      body: { preferences: [{ address: email, subject }] },
    });
    if (!raw?.nonce) {
      throw new AuthHttpError("No confirmation nonce was returned.", { status: null, code: "NO_CONFIRMATION_NONCE" });
    }
    return { nonce: raw.nonce };
  }

  /** Redeem the code the user received against the `nonce` from `requestEmailConfirmation`. */
  async confirmEmailConfirmation(nonce: string, code: string): Promise<void> {
    const channel = this.config.emailConfirmationChannel ?? "console";
    await this.http.request(`/notifications/${channel}/preferences/confirm`, {
      method: "POST",
      body: { nonce, token: Number(code) },
    });
  }
}
